import React, {
  ReactElement, Ref, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, ScrollView, ActivityIndicator, FlexStyle, Platform, KeyboardAvoidingView,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Style from '../constants/Style';
import Colors from '../constants/Colors';
import DeleteIcon24 from './24pxIcons/DeleteIcon24';
import Button from './Button';
import PressableStyled from './PressableStyled';

interface SelectionModalProps {
  isVisible: boolean,
  hide: () => void,
  listContent: ReactElement,
  title: string,
  apply?: () => void,
  buttonTitle?: string,
  loading?: boolean,
  style?: FlexStyle,
  scrollViewRef?: Ref<ScrollView>,
  scrollEnabled?: boolean,
  disableSwipeToClose?: boolean,
}

const SelectionModal = ({
  isVisible, hide, listContent, title, apply, loading, style, scrollViewRef, buttonTitle,
  scrollEnabled = true, disableSwipeToClose,
}: SelectionModalProps) => {
  const paddingBottom: number = useSafeAreaInsets().bottom;
  const [backdropPressHideEnabled, setBackdropPressHideEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (isVisible) {
      setBackdropPressHideEnabled(false);
      const timeout = setTimeout(() => {
        setBackdropPressHideEnabled(true);
        clearTimeout(timeout);
      }, 375);
    }
  }, [isVisible]);

  const onBackdropPress: () => void = () => (backdropPressHideEnabled ? hide() : null);

  return <Modal
    propagateSwipe
    {...{ isVisible, onBackdropPress }}
    onBackButtonPress={hide}
    onSwipeComplete={hide}
    swipeDirection={disableSwipeToClose ? undefined : 'down'}
    animationIn='slideInUp'
    animationOut='slideOutDown'
    statusBarTranslucent
    style={[Style.modal, styles.modal]}
    backdropColor={Colors.light.hereworksPrimaryGrey}
    backdropOpacity={0.40}
    backdropTransitionOutTiming={0}
  >
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'position' : 'height'}>
      <View style={[
        styles.container,
        { paddingBottom },
        apply && { maxHeight: styles.container.maxHeight + 80 },
        style,
      ]}>
        <Text style={styles.title} numberOfLines={1}>{title}</Text>
        <PressableStyled
          style={styles.closeButton}
          onPress={hide}
        >
          <DeleteIcon24 color={Colors.light.hereworksBlack600} />
        </PressableStyled>
        <ScrollView
          {...{ scrollEnabled }}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          ref={scrollViewRef}
        >
          {listContent}
        </ScrollView>
        {apply ? <Button
          title={buttonTitle ?? 'Apply'}
          affirmative
          onPress={apply}
          style={styles.applyButton}
          children={loading && <ActivityIndicator style={styles.loadingIndicator} />}
        /> : <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />}
      </View>
    </KeyboardAvoidingView>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: '#FFF',
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    padding: 12,
    paddingTop: 20,
    maxHeight: 284,
  },
  title: {
    fontSize: 14,
    lineHeight: 17.5,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    alignSelf: 'center',
    marginLeft: 24,
    marginRight: 34,
  },
  list: {
    marginTop: 12,
    marginHorizontal: -12,
  },
  listContent: {
    paddingHorizontal: 24,
    flexGrow: 1,
  },
  closeButton: {
    position: 'absolute',
    end: 24,
    top: 16,
  },
  applyButton: {
    flex: 0,
    alignSelf: 'stretch',
    margin: 12,
  },
  loadingIndicator: {
    alignSelf: 'center',
    position: 'absolute',
    end: '30%',
  },
});

export default SelectionModal;
